import React from 'react';
import authService from '../../api-authorization/AuthorizeService'
import axios from 'axios'
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import createCache from '@emotion/cache';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const muiCache = createCache({
    key: 'mui-datatables',
    prepend: true,
});

export default function DisplayPlayerProfile({ player }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Mobile-friendly info row component
    const InfoRow = ({ label, value }) => (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            py: 0.5,
            borderBottom: '1px solid #eee'
        }}>
            <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                    minWidth: isMobile ? 'auto' : '100px',
                    fontWeight: 500,
                    fontSize: isMobile ? '11px' : '12px'
                }}
            >
                {label}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ 
                    fontSize: isMobile ? '12px' : '14px',
                    wordBreak: 'break-word'
                }}
            >
                {value}
            </Typography>
        </Box>
    );

    return (
        <div className="mt-10 mb-10">
            { 
                <Box sx={{ pt: 1, pb: 1, px: isMobile ? 1 : 0 }}>
                    <Grid container direction="row" rowSpacing={1} columnSpacing={1} alignItems="flex-start">
                        <Grid item xs={12} lg={6}>
                            <Card>
                                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                                    <Typography 
                                        gutterBottom 
                                        variant={isMobile ? "subtitle1" : "h5"} 
                                        component="div" 
                                        color="text.primary"
                                        sx={{ fontWeight: 500, mb: isMobile ? 1 : 2 }}
                                    >
                                        FIDE: {player.fidePlayerId > 0 ? player.fideName : "No FIDE ID"}
                                    </Typography>
                                    
                                    <InfoRow 
                                        label="Membership" 
                                        value={player.fidePlayerId > 0 ? (
                                            <span>
                                                <a rel="noreferrer" href={'https://ratings.fide.com/profile/' + player.fidePlayerId} target='_blank'>{player.fidePlayerId}</a>
                                                {' ['}
                                                <a rel="noreferrer" href={'https://chess-results.com/PartieSuche.aspx?lan=1'} target='_blank'>{player.fideSex}</a>
                                                {', '}
                                                <a rel="noreferrer" href={'https://ratings.fide.com/view_games.phtml?id=' + player.fidePlayerId} target='_blank'>{player.fideBirthday}</a>
                                                {']'}
                                            </span>
                                        ) : "N/A"} 
                                    />
                                    
                                    <InfoRow 
                                        label="Country" 
                                        value={player.fidePlayerId > 0 ? player.fideCountry : "N/A"} 
                                    />
                                    
                                    <InfoRow 
                                        label="Title" 
                                        value={player.fidePlayerId > 0 
                                            ? `${player.fideTitle || ''} ${player.fideWomenTitle || ''} ${player.fideOtherTitle || ''}`.trim() || 'None'
                                            : "N/A"
                                        } 
                                    />
                                    
                                    {player.fidePlayerId > 0 && (
                                        isMobile ? (
                                            <Box sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '11px' }}>
                                                    OTB Rating
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                        <strong>Regular:</strong> {player.fideRating} [K{player.fidekValue}]
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                        <strong>Blitz:</strong> {player.fideBlitzRating} [K{player.fideBlitzKValue}]
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                        <strong>Rapid:</strong> {player.fideRapidRating} [K{player.fideRapidKValue}]
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <InfoRow 
                                                label="OTB Rating" 
                                                value={`Regular ${player.fideRating} [K${player.fidekValue}] | Blitz ${player.fideBlitzRating} [K${player.fideBlitzKValue}] | Rapid ${player.fideRapidRating} [K${player.fideRapidKValue}]`} 
                                            />
                                        )
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <Card>
                                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                                    <Typography 
                                        gutterBottom 
                                        variant={isMobile ? "subtitle1" : "h5"} 
                                        component="div" 
                                        color="text.primary"
                                        sx={{ fontWeight: 500, mb: isMobile ? 1 : 2 }}
                                    >
                                        USCF: {player.uscfPlayerId > 0 ? player.uscfName : "No USCF ID"}
                                    </Typography>
                                    
                                    <InfoRow 
                                        label="Membership" 
                                        value={player.uscfPlayerId > 0 ? (
                                            <span>
                                                <a rel="noreferrer" href={'https://ratings.uschess.org/player/' + player.uscfPlayerId} target='_blank'>{player.uscfPlayerId}</a>
                                                {' [Exp '}
                                                <a rel="noreferrer" href={'https://ratings.uschess.org/player/' + player.uscfPlayerId} target='_blank'>{player.uscfExpDate.split("T")[0]}</a>
                                                {']'}
                                            </span>
                                        ) : "N/A"} 
                                    />
                                    
                                    <InfoRow 
                                        label="State" 
                                        value={player.uscfPlayerId > 0 ? player.uscfState : "N/A"} 
                                    />
                                    
                                    {player.uscfPlayerId > 0 && (
                                        isMobile ? (
                                            <>
                                                <Box sx={{ mt: 1, py: 0.5, borderBottom: '1px solid #eee' }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '11px' }}>
                                                        OTB Rating
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                            <strong>Reg:</strong> {player.uscfRating}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                            <strong>Blitz:</strong> {player.uscfBlitzRating}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                            <strong>Quick:</strong> {player.uscfQuickRating}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ mt: 1, py: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '11px' }}>
                                                        Online Rating
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                            <strong>Reg:</strong> {player.uscfOnlineRating}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                            <strong>Blitz:</strong> {player.uscfOnlineBlitzRating}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                            <strong>Quick:</strong> {player.uscfOnlineQuickRating}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <InfoRow 
                                                    label="OTB Rating" 
                                                    value={`Regular ${player.uscfRating} | Blitz ${player.uscfBlitzRating} | Quick ${player.uscfQuickRating}`} 
                                                />
                                                <InfoRow 
                                                    label="Online Rating" 
                                                    value={`Regular ${player.uscfOnlineRating} | Blitz ${player.uscfOnlineBlitzRating} | Quick ${player.uscfOnlineQuickRating}`} 
                                                />
                                            </>
                                        )
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
                }
        
        </div>
    )
}
